import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Navbar } from "@/components/customer/Navbar";
import { Footer } from "@/components/customer/Footer";
import { WhatsAppButton } from "@/components/customer/WhatsAppButton";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatNumber, formatDate, ORDER_STATUS } from "@/lib/format";
import { useSettings, waLink } from "@/context/SettingsContext";

const TrackOrder = () => {
  const [query, setQuery] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const s = useSettings();

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setOrders(null);
    try {
      const isNumber = /^\d+$/.test(query.trim());
      const params = isNumber ? { order_number: query.trim() } : { contact: query.trim() };
      const { data } = await api.get("/orders/track", { params });
      setOrders(data);
    } catch (e) {
      toast.error(errMsg(e));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 pb-16">
        <div className="max-w-2xl mx-auto px-5">
          <div className="text-center mb-8">
            <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">Consultar pedido</h1>
            <p className="mt-3 text-slate-600">Ingresá tu número de pedido o el WhatsApp / email que usaste.</p>
          </div>

          <div className="flex gap-2 mb-8">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Ej: 1048 o +595 9XX XXXXXX"
              className="rounded-xl h-12"
              data-testid="track-input"
            />
            <Button onClick={search} disabled={loading} className="rounded-xl h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all" data-testid="track-search-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            </Button>
          </div>

          {orders && orders.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <PackageSearch className="h-12 w-12 mx-auto mb-3" />
              <p>No se encontró ningún pedido.</p>
            </div>
          )}

          <div className="space-y-4">
            {(orders || []).map((o) => (
              <div key={o.id} className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100" data-testid={`track-order-${o.order_number}`}>
                <div className="flex items-center justify-between mb-4">
                  <p className="font-heading font-bold text-lg text-slate-900">Pedido #{o.order_number}</p>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${ORDER_STATUS[o.status]?.color}`}>{ORDER_STATUS[o.status]?.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-y-3 text-sm">
                  <div><p className="text-slate-400 text-xs">Servicio</p><p className="font-medium text-slate-800">{o.service_name}</p></div>
                  <div><p className="text-slate-400 text-xs">Usuario</p><p className="font-medium text-slate-800">@{o.instagram_username}</p></div>
                  <div><p className="text-slate-400 text-xs">Cantidad</p><p className="font-medium text-slate-800">{formatNumber(o.quantity)}</p></div>
                  <div><p className="text-slate-400 text-xs">Valor</p><p className="font-medium text-slate-800">{formatGs(o.total)}</p></div>
                  <div><p className="text-slate-400 text-xs">Fecha</p><p className="font-medium text-slate-800">{formatDate(o.created_at)}</p></div>
                </div>
                <a href={waLink(s.whatsapp, `Hola, hice el pedido #${o.order_number} en Impulso Visual y necesito ayuda.`)} target="_blank" rel="noreferrer" className="mt-4 block">
                  <Button variant="outline" className="w-full rounded-xl border-slate-300 h-11">Contactar por WhatsApp</Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </main>
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default TrackOrder;
